import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { fetchPosts } from '@/lib/api';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function readTime(content) {
  if (!content) return '1 min read';
  const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

function BlogSkeleton() {
  return (
    <>
      {/* Featured skeleton */}
      <div className="animate-pulse mb-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="bg-brand-lavender rounded-2xl h-72 lg:h-96" />
          <div className="space-y-4">
            <div className="bg-brand-lavender rounded h-4 w-24" />
            <div className="bg-brand-lavender rounded h-10 w-3/4" />
            <div className="bg-brand-lavender rounded h-4 w-full" />
            <div className="bg-brand-lavender rounded h-4 w-2/3" />
          </div>
        </div>
      </div>
      {/* Grid skeleton */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-brand-lavender rounded-xl h-48 mb-4" />
            <div className="bg-brand-lavender rounded h-6 w-3/4 mb-2" />
            <div className="bg-brand-lavender rounded h-4 w-full" />
          </div>
        ))}
      </div>
    </>
  );
}

function FeaturedPost({ post }) {
  return (
    <motion.article {...fadeInUp} className="mb-16">
      <Link to={`/blog/${post.slug}`} className="group block">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image */}
          <div className="relative overflow-hidden rounded-2xl border-4 border-brand-lime">
            {post.cover_image ? (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-72 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-72 lg:h-96 bg-white flex items-center justify-center">
                <span className="font-black text-4xl text-black/10 uppercase">CatchingOn</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="service-pill text-xs">Latest</span>
            </div>
          </div>

          {/* Content */}
          <div>
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-bold uppercase tracking-wider text-black/60"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h2 className="text-3xl lg:text-4xl font-black text-black mb-4 group-hover:text-black/70 transition-colors duration-300 uppercase">
              {post.title}
            </h2>
            {post.excerpt && (
              <p className="text-lg text-black/60 mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-black/50 mb-6">
              {post.author && <span className="font-semibold text-black">{post.author}</span>}
              {post.publishedAt && (
                <>
                  <span>&middot;</span>
                  <span>{formatDate(post.publishedAt)}</span>
                </>
              )}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {readTime(post.content)}
              </span>
            </div>
            <span className="inline-flex items-center gap-2 btn-lime rounded-full text-sm group-hover:gap-3 transition-all">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function PostCard({ post, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link to={`/blog/${post.slug}`} className="group block h-full">
        <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden border-2 border-black hover-lift">
          {/* Image */}
          <div className="relative overflow-hidden">
            {post.cover_image ? (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-52 bg-brand-lavender flex items-center justify-center">
                <span className="font-black text-2xl text-black/10 uppercase">CatchingOn</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex flex-col flex-1 p-6">
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-bold uppercase tracking-wider text-black/50"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h3 className="text-xl font-black text-black mb-2 group-hover:text-black/70 transition-colors duration-300 uppercase">
              {post.title}
            </h3>
            {post.excerpt && (
              <p className="text-sm text-black/60 mb-4 flex-1 line-clamp-2">
                {post.excerpt}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-black/50 mt-auto pt-4 border-t border-black/10">
              {post.author && <span className="font-semibold text-black">{post.author}</span>}
              {post.author && post.publishedAt && <span>&middot;</span>}
              {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {readTime(post.content)}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts()
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const remaining = posts.slice(1);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="section-periwinkle pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <h1 className="text-5xl lg:text-6xl font-black text-black mb-6 uppercase leading-none">
              The <span className="bg-brand-lime px-2 inline-block">Blog</span>
            </h1>
            <p className="text-lg text-black/60">
              Marketing tips, social media strategies, and insights to help your
              business grow its online presence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts */}
      <section className="section-periwinkle pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <BlogSkeleton />
          ) : posts.length === 0 ? (
            <motion.div {...fadeInUp} className="text-center py-20">
              <p className="text-2xl font-black text-black mb-4 uppercase">Coming Soon</p>
              <p className="text-black/60 mb-8">
                We're working on our first articles. Check back soon!
              </p>
              <Link to="/contact" className="btn-lime rounded-full inline-block">
                Get in Touch
              </Link>
            </motion.div>
          ) : (
            <>
              {featured && <FeaturedPost post={featured} />}

              {remaining.length > 0 && (
                <>
                  <motion.div {...fadeInUp}>
                    <div className="border-t-2 border-black/10 pt-12 mb-8">
                      <h2 className="text-2xl font-black text-black uppercase">
                        More Articles
                      </h2>
                    </div>
                  </motion.div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {remaining.map((post, index) => (
                      <PostCard key={post.slug} post={post} index={index} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6 uppercase">
              Ready to Grow Your Brand?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              From social media management to full marketing strategies — let's get
              your business catching on.
            </p>
            <Link to="/contact" className="btn-lime rounded-full inline-block text-lg">
              Let's Chat
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
